// NEW_FILE_JUSTIFIED: State management hook for template-driven claim application wizard — mirrors usePolicyApplicationWizard but for claims

import { useState, useCallback } from 'react';
import { useMutation } from 'convex/react';
import { toast } from 'sonner';
import { api } from '@convex/_generated/api';
import type { Id } from '@convex/_generated/dataModel';
import type { IClaimApplicationData, TClaimantType } from '@/@types/claims';
import type { IFormStep } from '@/@types/policyFormTemplates';

// LOCAL_TYPE_JUSTIFIED: hook args interface co-located with the hook — not a component file
interface IUseClaimApplicationWizardArgs {
  templateSteps: IFormStep[];
  templateId: Id<'claimFormTemplates'>;
  orgId: Id<'organisations'>;
  policyId: Id<'insurancePolicies'>;
  existingClaimId?: Id<'claims'>;
  existingData?: IClaimApplicationData;
  claimantType: TClaimantType;
  claimantId: string;
}

// LOCAL_TYPE_JUSTIFIED: hook return interface co-located with the hook — not a component file
interface IUseClaimApplicationWizardReturn {
  currentStep: number;
  values: Record<string, unknown>;
  goToStep: (step: number) => void;
  nextStep: () => void;
  prevStep: () => void;
  updateValue: (key: string, value: unknown) => void;
  saveDraft: () => Promise<void>;
  submitClaim: () => Promise<void>;
  isSaving: boolean;
  isSubmitting: boolean;
  claimId: Id<'claims'> | null;
}

export function useClaimApplicationWizard({
  templateSteps,
  templateId,
  orgId,
  policyId,
  existingClaimId,
  existingData,
  claimantType,
  claimantId,
}: IUseClaimApplicationWizardArgs): IUseClaimApplicationWizardReturn {
  const [currentStep, setCurrentStep] = useState(existingData?.currentStep ?? 0);
  const [values, setValues] = useState<Record<string, unknown>>(existingData?.values ?? {});
  const [claimId, setClaimId] = useState<Id<'claims'> | null>(existingClaimId ?? null);
  const [isSaving, setIsSaving] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const saveDraftMutation = useMutation(api.insurance.claims.saveDraft);
  const updateDraftMutation = useMutation(api.insurance.claims.updateDraft);
  const submitDraftMutation = useMutation(api.insurance.claims.submitDraft);

  const totalSteps = templateSteps.length;

  const goToStep = useCallback(
    (step: number) => {
      setCurrentStep(Math.max(0, Math.min(step, totalSteps - 1)));
    },
    [totalSteps]
  );

  const nextStep = useCallback(() => {
    setCurrentStep(s => Math.min(s + 1, totalSteps - 1));
  }, [totalSteps]);

  const prevStep = useCallback(() => {
    setCurrentStep(s => Math.max(s - 1, 0));
  }, []);

  const updateValue = useCallback((key: string, value: unknown) => {
    setValues(prev => ({ ...prev, [key]: value }));
  }, []);

  const saveDraft = useCallback(async () => {
    setIsSaving(true);
    try {
      const applicationData: IClaimApplicationData = { currentStep, values };

      if (claimId) {
        await updateDraftMutation({
          claimId,
          claimApplicationData: applicationData,
          currentStep,
        });
        toast.success('Draft updated');
      } else {
        const newId = await saveDraftMutation({
          orgId,
          policyId,
          claimantType,
          claimantId,
          formTemplateId: templateId,
          claimApplicationData: applicationData,
          currentStep,
        });
        setClaimId(newId);
        toast.success('Draft saved');
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to save draft';
      toast.error(msg);
    } finally {
      setIsSaving(false);
    }
  }, [
    currentStep,
    values,
    claimId,
    orgId,
    policyId,
    claimantType,
    claimantId,
    templateId,
    saveDraftMutation,
    updateDraftMutation,
  ]);

  const submitClaim = useCallback(async () => {
    setIsSubmitting(true);
    try {
      const applicationData: IClaimApplicationData = { currentStep, values };

      if (claimId) {
        await updateDraftMutation({
          claimId,
          claimApplicationData: applicationData,
          currentStep,
        });
        await submitDraftMutation({ claimId });
      } else {
        const newId = await saveDraftMutation({
          orgId,
          policyId,
          claimantType,
          claimantId,
          formTemplateId: templateId,
          claimApplicationData: applicationData,
          currentStep,
        });
        setClaimId(newId);
        await submitDraftMutation({ claimId: newId });
      }
      toast.success('Claim submitted');
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to submit claim';
      toast.error(msg);
    } finally {
      setIsSubmitting(false);
    }
  }, [
    currentStep,
    values,
    claimId,
    orgId,
    policyId,
    claimantType,
    claimantId,
    templateId,
    saveDraftMutation,
    updateDraftMutation,
    submitDraftMutation,
  ]);

  return {
    currentStep,
    values,
    goToStep,
    nextStep,
    prevStep,
    updateValue,
    saveDraft,
    submitClaim,
    isSaving,
    isSubmitting,
    claimId,
  };
}
